import React, { useContext, useEffect, useRef, useState } from "react";

/**
 * fix for runtime error caused by crypto
 * see https://stackoverflow.com/questions/68707553/uncaught-referenceerror-buffer-is-not-defined
 */
import { Buffer } from "buffer";
window.Buffer = Buffer;

import {
    useActiveChats,
    useMatrixChatList,
    useChatMessage,
    useCreateChatModal,
    useDeleteChat,
    useEditChat,
    useBackupForm,
    useTypingListener,
    useChatListUnread,
    useSendMessage
} from "src/scenes/Chat/hooks";
import { UserKeys } from "src/constants/userDetails";
import { UserContext } from "src/scenes/App/UserContext";
import { ChatContext } from "./ChatContext";
import { BackupKeyContext } from "src/scenes/Chat/BackupKeyContext";

import { MainContainer, LeftContainer, RightContainer } from "src/scenes/Chat/components/styled/shared";
import ChatList from "./ChatList";
import ChatMessageContainer from "./ChatMessageContainer";
import CreateChatModal from "src/scenes/Chat/components/CreateChatModal";
import DeleteConfirmation from "src/scenes/Chat/components/DeleteConfirmation";
import ProfileSettingsModal from "src/scenes/Chat/components/ProfileSettingsModal";
import BackupKeyModal from "./components/keyBackup/BackupKeyModal";
import ChatEncryptionComingSoonModal from "./components/ChatEncryptionComingSoonModal";
import ChatPageSkeleton from "src/scenes/Chat/components/ChatPageSkeleton";
import { PageContainer } from "src/components";

const noop = () => undefined;

const Chat = (props) => {
    useEffect(() => {
        props.isChatRendered(true);

        return () => {
            props.isChatRendered(false);
        };
    }, []);
    const propagateUpdatedChat = apiResponseData => {
        updateOpenedMatrixChatWithFormData(apiResponseData);
        updateResponseDataInMatrixChatList(apiResponseData);
    };

    const userContext = useContext(UserContext);
    const matrixSession = useContext(ChatContext) || {};
    const { appFns } = userContext;
    const headers = matrixSession.headers || { headers: { Authorization: "Bearer " + userContext[UserKeys.TOKEN] } };

    const [currentMatrixUserId, setCurrentMatrixUserUuid] = useState(matrixSession.currentMatrixUserId || userContext[UserKeys.MATRIX_USER_ID]);
    const [hasCompletedInitialChatLoad, setHasCompletedInitialChatLoad] = useState(false);
    const hasRequestedChatSessionRefresh = useRef(false);

    const { 
        matrixClient, 
        isInitialSyncComplete,
        matrixRecoveryRequired,
        chatBackupRecoveryRequired,
        acknowledgeChatBackupRecovery,
        runListener
    } = matrixSession;

    const {
        onlineUsersMatrixId = [],
        handleSetOwnUserPresence = noop
    } = matrixSession.usePresenceListener || {};
    
    const {
        activeChatListDetails,
        isFetchingActiveChats,
        businessBackgroundColor,
        selectedChatListTypeOrLeadershipUuid,
        handleChatListSelectChange,
        chatList,
        leadershipRoles,
        populateActiveChatListDetails,
        lookingForLeadershipToChat,
        setLookingForLeadershipToChat,
        selectedChatListTypeOrLeadershipUuidLabel,
        removeRoomInActiveChatListDetails
    } = useActiveChats(matrixSession.chatCollections);

    const { 
        activeChatsTotalUnread,
        updateActiveChatListTotalUnread 
    } = useChatListUnread(matrixClient, activeChatListDetails);

    const {
        activeChatList,
        updateResponseDataInMatrixChatList,
        removeRoomInMatrixChatList,
        upsertRoomInMatrixChatList,
        insertChatCreateResponseToMatrixChats, 
        isHydratingMatrixChatList,
        placedRoomToIndexZeroOfMatrixChatList
    } = useMatrixChatList(chatList, selectedChatListTypeOrLeadershipUuid, matrixClient, populateActiveChatListDetails, removeRoomInActiveChatListDetails, updateActiveChatListTotalUnread);

    const {
        openedMatrixChat,
        setOpenedMatrixChat,
        updateOpenedMatrixChatWithFormData,
        upsertRoomTimeline,
        isFetchingRoomTimeline,
        roomTimeline,
        loadedRoomTimelineRoomId,
        toggleDropdown,
        setToggleDropdown,
        displayOtherUserProfileModal,
        displayProfileModal,
        closeProfileModal,
        profileModalIsShown,
        profileModalShowsCurrentUser,
        profileModalUser,
        profileModalUserLeadershipRoles,
        encryptionComingSoonModalIsShown,
        setEncryptionComingSoonModalIsShown
    } = useChatMessage(activeChatList, currentMatrixUserId);

    useEffect(() => {
        if (!isInitialSyncComplete || !matrixClient || !runListener) {
            return;
        }

        let cleanupMatrixListeners = null;
        let isCancelled = false;
        const sessionUserId = matrixClient.getUserId();
        if (currentMatrixUserId !== sessionUserId) {
            setCurrentMatrixUserUuid(sessionUserId);
            appFns.updateUserContextProperties({ [UserKeys.MATRIX_USER_ID]: sessionUserId });
        }

        cleanupMatrixListeners = runListener(
            matrixClient,
            removeRoomInMatrixChatList,
            upsertRoomInMatrixChatList,
            upsertRoomTimeline,
            placedRoomToIndexZeroOfMatrixChatList,
            openedMatrixChat,
            setOpenedMatrixChat
        );

        (async () => {
            await appFns.reinitialiseUser();
            await runBackupKeyRequirementChecks();
        })().catch(error => {
            if (!isCancelled) {
                console.warn("[Matrix] Background chat security check failed:", error);
            }
        });

        return () => {
            isCancelled = true;
            if (cleanupMatrixListeners) cleanupMatrixListeners();
        };
    }, [isInitialSyncComplete, matrixClient]);

    const { 
        showCreateChatModalOrShowLeadershipEmployees, 
        chatToCreate,
        closeCreateChatModal,
        handleCreateChat,
        handleCreateChatResponse,
        isCurrentlyCreatingChat,
        createChatWithLeadership
    } = useCreateChatModal(leadershipRoles, insertChatCreateResponseToMatrixChats, setOpenedMatrixChat, setLookingForLeadershipToChat, handleChatListSelectChange);

    const {
        showEditGroupChatModal,
        setShowEditGroupChatModal,
        updateGroupChat,
        isUpdatingChat
    } = useEditChat(propagateUpdatedChat);

    const { 
        isDeletingChat,
        showConfirmationModal, 
        handleChatDeletion, 
        setShowConfirmationModal, 
        handleShowDeleteConfirmationModal 
    } = useDeleteChat(setShowEditGroupChatModal);

    const {
        handleShowBackupKeyModal,
        showBackupRestoreForm,
        closeBackupKeyForm,
        runBackupKeyRequirementChecks,
        setUserHasBackupKeys,
        updateBackupKeyFormToShow,
        backupKeyChecksAreComplete,
        showBackupKeyForm,
        backupFormToShow,
        setBackupFormToShow
    } = useBackupForm(matrixClient);

    useEffect(() => {
        if (!chatBackupRecoveryRequired) {
            return;
        }

        showBackupRestoreForm();
        acknowledgeChatBackupRecovery?.();
    }, [chatBackupRecoveryRequired]);

    const { 
        isTyping, 
        typingMember, 
        typingRoomId 
    } = useTypingListener(matrixClient);

    
    const {
        newMessage,
        queued,
        newMessageInReplyToEvent,
        editMessageEvent,
        attachments,
        setNewMessage,
        prepareMessage,
        onSetNewMessageInReplyToEventHandler,
        onSetEditMessageEventHandler,
        onResetInputMessageEvents,
        setAttachments,
        resendMessage,
        resentEventIds
    } = useSendMessage(openedMatrixChat, matrixClient);

    const {
        callState,
        activeGroupCall,
        isSendingCallAction,
        localStream,
        remoteStream,
        remoteStreams,
        callChat,
        callLobby,
        callWindowRef,
        transcriptionState,
        screenShareIsActive,
        rememberKnownChats,
        startCall,
        openCallLobby,
        closeCallLobby,
        focusCallWindow,
        startCallFromLobby,
        setLocalAudioEnabled,
        setLocalMicrophoneDevice,
        setLocalVideoEnabled,
        setLocalCameraDevice,
        setLocalSpeakerDevice,
        toggleScreenShare,
        setLocalBackgroundEffect,
        answerCall,
        answerCallFromLobby,
        joinGroupCall,
        hangupCall,
        rejectCall,
        requestCallTranscription,
        requestCallSummary,
        setAlwaysTakeAiNotes,
        publishTranscriptSegment
    } = matrixSession.useChatCalls || {};

    const initialChatDataIsReady = matrixRecoveryRequired || (
        !isFetchingActiveChats &&
        !isHydratingMatrixChatList &&
        openedMatrixChat !== undefined
    );
    const initialChatPageIsLoading = !hasCompletedInitialChatLoad && !initialChatDataIsReady;

    useEffect(() => {
        matrixSession.useChatCalls?.rememberKnownChats?.([
            openedMatrixChat,
            ...(activeChatList || [])
        ]);
    }, [activeChatList, matrixSession.useChatCalls, openedMatrixChat]);

    useEffect(() => {
        if (matrixClient || matrixSession.matrixIsEnabled !== false || hasRequestedChatSessionRefresh.current) {
            return;
        }

        hasRequestedChatSessionRefresh.current = true;
        appFns.reinitialiseUser();
    }, [appFns, matrixClient, matrixSession.matrixIsEnabled]);

    useEffect(() => {
        if (!hasCompletedInitialChatLoad && initialChatDataIsReady) {
            setHasCompletedInitialChatLoad(true);
        }
    }, [hasCompletedInitialChatLoad, initialChatDataIsReady]);

    return (
        <PageContainer style={{ padding: "0 1rem" }}>
            <ChatContext.Provider value={{
                headers: headers,
                currentMatrixUserId: currentMatrixUserId,
                matrixClient: matrixClient, //useMatrixClient
                isInitialSyncComplete: isInitialSyncComplete, //useMatrixClient
                matrixRecoveryRequired: matrixRecoveryRequired,
                useActiveChats: {
                    activeChatListDetails: activeChatListDetails,
                    isFetchingActiveChats: isFetchingActiveChats,
                    businessBackgroundColor: businessBackgroundColor,
                    selectedChatListTypeOrLeadershipUuid: selectedChatListTypeOrLeadershipUuid,
                    handleChatListSelectChange: handleChatListSelectChange,
                    chatList: chatList,
                    leadershipRoles: leadershipRoles,
                    lookingForLeadershipToChat: lookingForLeadershipToChat,
                    selectedChatListTypeOrLeadershipUuidLabel: selectedChatListTypeOrLeadershipUuidLabel,
                    setLookingForLeadershipToChat: setLookingForLeadershipToChat
                },
                useChatListUnread: {
                    activeChatsTotalUnread: activeChatsTotalUnread,
                    updateActiveChatListTotalUnread: updateActiveChatListTotalUnread
                },
                useMatrixChatList: {
                    activeChatList: activeChatList,
                    isHydratingMatrixChatList: isHydratingMatrixChatList,
                    updateResponseDataInMatrixChatList: updateResponseDataInMatrixChatList,
                    upsertRoomInMatrixChatList: upsertRoomInMatrixChatList
                },
                useChatMessage: {
                    openedMatrixChat: openedMatrixChat,
                    setOpenedMatrixChat: setOpenedMatrixChat,
                    updateOpenedMatrixChatWithFormData: updateOpenedMatrixChatWithFormData,
                    isFetchingRoomTimeline: isFetchingRoomTimeline,
                    roomTimeline: roomTimeline,
                    loadedRoomTimelineRoomId: loadedRoomTimelineRoomId,
                    toggleDropdown: toggleDropdown,
                    setToggleDropdown: setToggleDropdown,
                    displayOtherUserProfileModal: displayOtherUserProfileModal,
                    displayProfileModal: displayProfileModal,
                    setEncryptionComingSoonModalIsShown: setEncryptionComingSoonModalIsShown
                },
                useCreateChatModal: {
                    showCreateChatModalOrShowLeadershipEmployees: showCreateChatModalOrShowLeadershipEmployees,
                    chatToCreate: chatToCreate,
                    closeCreateChatModal: closeCreateChatModal,
                    handleCreateChat: handleCreateChat,
                    handleCreateChatResponse: handleCreateChatResponse,
                    isCurrentlyCreatingChat: isCurrentlyCreatingChat,
                    createChatWithLeadership: createChatWithLeadership
                },
                useDeleteChat: {
                    isDeletingChat: isDeletingChat,
                    handleChatDeletion: handleChatDeletion, 
                    handleShowDeleteConfirmationModal: handleShowDeleteConfirmationModal,
                    showConfirmationModal: showConfirmationModal
                },
                useEditChat: {
                    showEditGroupChatModal,
                    setShowEditGroupChatModal,
                    updateGroupChat,
                    isUpdatingChat
                },
                useTypingListener: {
                    isTyping: isTyping, 
                    typingMember: typingMember, 
                    typingRoomId: typingRoomId
                },
                usePresenceListener: {
                    onlineUsersMatrixId: onlineUsersMatrixId,
                    handleSetOwnUserPresence: handleSetOwnUserPresence
                },
                useBackupForm: {
                    handleShowBackupKeyModal: handleShowBackupKeyModal,
                    closeBackupKeyForm: closeBackupKeyForm,
                    runBackupKeyRequirementChecks: runBackupKeyRequirementChecks,
                    setUserHasBackupKeys: setUserHasBackupKeys,
                    backupKeyChecksAreComplete: backupKeyChecksAreComplete,
                    showBackupKeyForm: showBackupKeyForm,
                    backupFormToShow: backupFormToShow
                },
                useSendMessage: {
                    newMessage: newMessage,
                    queued: queued,
                    newMessageInReplyToEvent: newMessageInReplyToEvent,
                    editMessageEvent: editMessageEvent,
                    attachments: attachments,
                    setNewMessage: setNewMessage,
                    prepareMessage: prepareMessage,
                    onSetNewMessageInReplyToEventHandler: onSetNewMessageInReplyToEventHandler,
                    onSetEditMessageEventHandler: onSetEditMessageEventHandler,
                    onResetInputMessageEvents: onResetInputMessageEvents,
                    setAttachments: setAttachments,
                    resendMessage: resendMessage,
                    resentEventIds: resentEventIds
                },
                useChatCalls: {
                    callState: callState,
                    activeGroupCall: activeGroupCall,
                    isSendingCallAction: isSendingCallAction,
                    localStream: localStream,
                    remoteStream: remoteStream,
                    remoteStreams: remoteStreams,
                    callChat: callChat,
                    callLobby: callLobby,
                    callWindowRef: callWindowRef,
                    transcriptionState: transcriptionState,
                    screenShareIsActive: screenShareIsActive,
                    rememberKnownChats: rememberKnownChats,
                    startCall: startCall,
                    openCallLobby: openCallLobby,
                    closeCallLobby: closeCallLobby,
                    focusCallWindow: focusCallWindow,
                    startCallFromLobby: startCallFromLobby,
                    setLocalAudioEnabled: setLocalAudioEnabled,
                    setLocalMicrophoneDevice: setLocalMicrophoneDevice,
                    setLocalVideoEnabled: setLocalVideoEnabled,
                    setLocalCameraDevice: setLocalCameraDevice,
                    setLocalSpeakerDevice: setLocalSpeakerDevice,
                    toggleScreenShare: toggleScreenShare,
                    setLocalBackgroundEffect: setLocalBackgroundEffect,
                    answerCall: answerCall,
                    answerCallFromLobby: answerCallFromLobby,
                    joinGroupCall: joinGroupCall,
                    hangupCall: hangupCall,
                    rejectCall: rejectCall,
                    requestCallTranscription: requestCallTranscription,
                    requestCallSummary: requestCallSummary,
                    setAlwaysTakeAiNotes: setAlwaysTakeAiNotes,
                    publishTranscriptSegment: publishTranscriptSegment
                }
            }} >
                {initialChatPageIsLoading ? (
                    <ChatPageSkeleton />
                ) : (
                    <MainContainer> 
                        <LeftContainer>
                            { <ChatList props={props}/> }
                        </LeftContainer>
                        <RightContainer>
                            { <ChatMessageContainer /> }
                        </RightContainer>
                    </MainContainer>
                )}
                {chatToCreate.showModal &&
                    <CreateChatModal />
                }
                {showConfirmationModal &&
                    <DeleteConfirmation
                        isDeletingChat={isDeletingChat}
                        showDialog={showConfirmationModal}
                        handleClose={() => setShowConfirmationModal(false)}
                        confirm={handleChatDeletion}
                    />
                }
                {profileModalIsShown &&
                    <ProfileSettingsModal
                        handleClose={closeProfileModal}
                        isCurrentUser={profileModalShowsCurrentUser}
                        userToShow={profileModalUser}
                        profileModalUserLeadershipRoles={profileModalUserLeadershipRoles}
                    />
                }
                { showBackupKeyForm && backupFormToShow &&
                    <BackupKeyContext.Provider value={{
                        backupFormToShow: backupFormToShow,
                        setUserHasBackupKeys: setUserHasBackupKeys,
                        closeBackupKeyForm: closeBackupKeyForm,
                        setBackupFormToShow: setBackupFormToShow,
                        updateBackupKeyFormToShow: updateBackupKeyFormToShow,
                    }}>
                        <BackupKeyModal showDialog={showBackupKeyForm} />
                    </BackupKeyContext.Provider>
                }
                { encryptionComingSoonModalIsShown &&
                    <ChatEncryptionComingSoonModal />
                }
            </ChatContext.Provider>
        </PageContainer>
    );
};

export default Chat;
